import { Box, Button, Checkbox, Flex, Grid, Tbody, Text, Tfoot, Th, Thead, useBoolean, useToast } from '@chakra-ui/react'
import type { InsuranceParamsGridItem, InsuranceParamsVehicleGridItem } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useContext, useState } from 'react'
import { Pagination, Table, TableData, TableHeader, TableRow } from '../../components/Grid'
import { Loading } from '../../components/Loading'
import { useApp } from '../../hooks/useApp'
import { useDeleteInsuranceParams } from '../../hooks/useInsuranceParams'
import { conceptsCarsContext } from '../../pages/vehicle/concepts'

import { conceptsPropertyContext } from '@/pages/property/concepts'
import { conceptsTravelContext } from '@/pages/travel/concepts'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { ConceptRow } from './ConceptRow'

export const getConceptsContext = (product: Product) => {
  switch (product) {
    case Product.TravelInsurance: return conceptsTravelContext
    case Product.VehicleInsurance:
    case Product.FleetInsurance:
    case Product.VehicleLeasingInsurance: return conceptsCarsContext
    case Product.LiabilityInsurance:
    case Product.PropertyInsurance: return conceptsPropertyContext
    default: throw Error('Chybí concepts context')
  }
}

export const TableWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ maxW: '100%', overflow: 'hidden', overflowX: 'auto' }}>{children}</Box>
}

export const ConceptsList = ({
  loading,
  concepts,
  leftFooterPart,
  withPaginator = false,
  allowSelect = false,
}: {
  loading?: boolean
  concepts: InsuranceParamsGridItem[]
  withPaginator?: boolean
  allowSelect?: boolean
  leftFooterPart?: React.ReactNode
}) => {
  const { product } = useApp()
  const context = getConceptsContext(product!)
  const { translate } = useTranslationsContext()
  const { currentPage, pageSize, totalCount, setCurrentPage, reload, ...rest } = useContext(context)
  loading = loading || rest.loading

  const [selectedRows, setSelectedRows] = useState<InsuranceParamsVehicleGridItem['insuranceParamsId'][]>([])
  const [confirmDelete, setConfirmDelete] = useBoolean(false)
  const [isDeleting, setIsDeleting] = useBoolean(false)
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < concepts.length
  const isChecked = selectedRows.length === concepts.length
  const toast = useToast()
  const { mutateAsync: deleteInsuranceParams } = useDeleteInsuranceParams()

  const handleCheckboxChange = () => {
    if (selectedRows.length === concepts.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(concepts.map((concept) => concept.insuranceParamsId))
    }
  }

  const handleRowSelect = (id: InsuranceParamsVehicleGridItem['insuranceParamsId']) => {
    const isSelected = selectedRows.includes(id)
    let newSelectedRows = selectedRows

    if (isSelected) {
      // Remove when is currently selected
      newSelectedRows = selectedRows.filter((rowId) => rowId !== id)
    } else {
      // Add when not selected yet
      newSelectedRows = [...newSelectedRows, id]
    }

    setSelectedRows(() => newSelectedRows)
  }

  const handleDelete = () => {
    if (confirmDelete) {
      setIsDeleting.on()
      deleteInsuranceParams({ paramIds: selectedRows })
        .then((resp) => {
          setIsDeleting.off()
          setSelectedRows([])
          setConfirmDelete.off()
          reload()
          if (!resp.data.success && resp.data.errors) {
            toast({
              title: 'Nepodařilo se smazat nabídku: ' + resp.data.errors?.join(),
              status: 'error',
              duration: 5000,
              isClosable: true
            })
          }
        })
        .catch((err) => console.log('error delete param ids', err))
    } else {
      setConfirmDelete.on()
    }
  }

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <TableWrapper>
        <Table cellPadding="0" cellSpacing="0">
          <Thead>
            <TableRow hoverEffect={false}>
              <Th display={allowSelect ? 'table-cell' : 'none'} w='60px' >
                <Checkbox
                  gap={0}
                  isIndeterminate={isIndeterminate}
                  isChecked={isChecked}
                  onChange={handleCheckboxChange}
                />
              </Th>

              <TableHeader>
                <p>{translate('insuranceParamsId', 'Číslo nabídky')}</p>
                <p>{translate('status', 'Stav')}</p>
              </TableHeader>

              <TableHeader>
                <p>{translate('dateCreated', 'Datum vytvoření')}</p>
                <p>{translate('startDate', 'Datum počátku')}</p>
              </TableHeader>

              <TableHeader sx={{ px: 0 }}></TableHeader>

              <TableHeader sx={{ pl: 0 }}>
                {product == Product.VehicleLeasingInsurance && <p>{translate('client', 'Klient')}</p>}
                {product != Product.VehicleLeasingInsurance && <p>{translate('policyholder', 'Pojistník')}</p>}
                {[Product.VehicleInsurance, Product.VehicleLeasingInsurance].includes(product!) && <p>{translate('vehicle', 'Vozidlo')}</p>}
              </TableHeader>

              <TableHeader>

              </TableHeader>

              <TableHeader>
                <p>{translate('conceptName', 'Název nabídky')}</p>
                <p>{translate('processedBy', 'Zpracoval')}</p>
              </TableHeader>
            </TableRow>
          </Thead>

          <Tbody>
            {!loading && ((concepts?.length ?? 0) > 0) &&
              concepts.map((concept) => {
                return <ConceptRow
                  key={`${concept.insuranceParamsId}_${concept.offerId}`}
                  allowSelect={allowSelect}
                  selectedRows={selectedRows}
                  onRowSelect={handleRowSelect}
                  item={concept} />
              })}

            {!loading && ((concepts?.length ?? 0) == 0) &&
              <TableRow>
                <TableData colSpan={allowSelect ? 7 : 6} py={8} textAlign={'center'}>
                  <Text>{translate('noData', 'Žádné nabídky k zobrazení')}</Text>
                </TableData>
              </TableRow>
            }

            {loading &&
              <TableRow>
                <TableData colSpan={allowSelect ? 7 : 6} py={8}>
                  <Loading large={true} />
                </TableData>
              </TableRow>
            }
          </Tbody>

          {(allowSelect || withPaginator) && !loading &&

            <Tfoot>
              <TableRow hoverEffect={false}>
                <TableData colSpan={allowSelect ? 7 : 6}>
                  <Grid gridTemplateColumns='1fr auto 1fr'>
                    {leftFooterPart && leftFooterPart}
                    {!leftFooterPart && (!allowSelect || selectedRows.length == 0) && <Box></Box>}

                    {allowSelect && selectedRows.length > 0 && (
                      <Flex sx={{ gap: 2 }}>
                        {confirmDelete && !isDeleting && (
                          <Button onClick={setConfirmDelete.off}>
                            {translate('cancel', 'Zrušit')}
                          </Button>
                        )}

                        <Button
                          colorScheme="red"
                          onClick={handleDelete}
                          disabled={isDeleting}
                        >
                          {/* <Trash /> */}
                          {!isDeleting && (
                            confirmDelete
                              ? `${translate('confirmDelete', 'Opravdu smazat?')} (${selectedRows.length})`
                              : `${translate('delete', 'Smazat')} (${selectedRows.length})`
                          )}
                          {isDeleting && (translate('deleting', 'Mazání') + '...')}
                        </Button>
                      </Flex>
                    )}

                    {withPaginator && (
                      <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={10} pageSize={pageSize} />
                    )}
                  </Grid>
                </TableData>
              </TableRow>
            </Tfoot>}
        </Table>
      </TableWrapper >
    </Box>
  )
}
